/**
 * Information from the route (such as current app id, template slug, searches)
 * was in a format that was indigestible by analytics (full path instead of
 * individual items, parsable object values, etc). It seems it is easier for us
 * to put it in this format in state rather than ask whoever is managing
 * analytics to do this deconstruction themselves.
 */

import { Reducer } from "redux"
import { RouterAction, LOCATION_CHANGE } from "connected-react-router"
import { searchStringToObj } from "../../Helpers/Functions/urlParse"
import { matchPath } from "react-router"
import { abstractRoutePaths } from "../../Routes"
import { AuthUserType, RouteInfoType } from "../../Actions/Types"
import { UserAction } from "../Auth/UserReducer"

export enum ExplanationModalType {
    timeout = "timeout",
    connectionLost = "connectionLost"
}

export interface RouteInfoState {
    pathname?: {
        complete: string
        components: string[]
    }
    search?: {
        complete: string
        components: {
            [index: string]: string
        }
    }
    hash?: string
    isInModalRoute: boolean
    wasInModalRoute: boolean
    explanationModal: {
        visible: boolean
        type: ExplanationModalType
    }
}

const initialState: RouteInfoState = {
    isInModalRoute: false,
    wasInModalRoute: false,
    explanationModal: {
        visible: false,
        type: ExplanationModalType.timeout
    }
}

interface ShowTimeoutExplanationModalAction {
    type: RouteInfoType
    payload: {
        visible: boolean
        type: ExplanationModalType
    }
}

export type RouteInfoAction = ShowTimeoutExplanationModalAction

export const RouteInfoReducer: Reducer<
    RouteInfoState,
    RouteInfoAction | RouterAction | UserAction
> = (state = initialState, action): RouteInfoState => {
    switch (action.type) {
        case LOCATION_CHANGE:
            const { pathname, search, hash } = action.payload.location
            return {
                pathname: {
                    complete: pathname,
                    components: pathname
                        .split("/")
                        .reduce(
                            (all, p) => [...all, ...(p !== "" ? [p] : [])],
                            [] as string[]
                        )
                },
                hash: hash !== "" ? hash.substr(1) : undefined,
                search:
                    search !== ""
                        ? {
                              complete: search,
                              components: searchStringToObj(search)
                          }
                        : undefined,
                isInModalRoute:
                    matchPath(pathname, {
                        path: abstractRoutePaths.jobsDetail
                    }) !== null ||
                    matchPath(pathname, {
                        path: abstractRoutePaths.jobsMessageDetail
                    }) !== null ||
                    matchPath(pathname, {
                        path: abstractRoutePaths.appCustomizeBaselineDataDetail
                    }) !== null,
                wasInModalRoute: state.isInModalRoute,
                explanationModal: state.explanationModal
            }
        case RouteInfoType.showTimeoutExplanationModal:
            return {
                ...state,
                explanationModal: action.payload
            }
        case AuthUserType.signedOut:
            return initialState
        default:
            return state
    }
}
